import { z } from "zod";
import { BuyOrderRow } from "./components/BuyOrders";
import { SellOrderRow } from "./components/SellOrders";

export const orderFormSchema = z
  .object({
    dpPortfolioId: z.string().min(1, "Portfolio is required"),
    dpPortfolioName: z.string().min(1, "Portfolio name is required"),
    dpPortType: z.string().min(1, "Portfolio type is required"),
    dpModelType: z.string().min(1, "Model type is required"),
    dpModelTypeName: z.string(),
    dpAmountToInvest: z.number().min(0),
    dpSumAvCash: z.number().min(0),
    dpSumInsValue: z.number().min(0),
    dpOnOffPercentage: z.string().refine(
      (val) => {
        const num = Number(val);
        return !isNaN(num) && num >= 0 && num <= 3;
      },
      { message: "Must be a number between 0 and 3" }
    ),
    dpManagementFee: z.string().min(1, "Management fee is required"),
    dpFeeType: z.string().min(1, "Fee type is required"),
    dpOnOffAmount: z.number().min(0),
    dpOnOffDate: z.string().optional(),
    dpAccount: z.string().optional(),
    dpNewAccount: z.string().optional(),
    dpWithdrawal: z.number().min(0),
    ordreModul: z.string(),
    dpServiceType: z.string().optional(),
    buyOrders: z.array(z.any()).default([]),
    sellOrders: z.array(z.any()).default([]),
    dpSavingsEnabled: z.boolean().default(false),
    dpSavingsAmount: z.number().min(0).optional(),
    dpSavingsDayOfMonth: z.number().min(1).max(31).optional(),
    dpSavingsAvtalegiro: z.boolean().default(false),
    dpSavingsFunds: z
      .array(z.object({ name: z.string(), pct: z.number().min(0).max(100) }))
      .max(5)
      .default([]),
    dpWithdrawalEnabled: z.boolean().default(false),
    dpWdAccount: z.string().optional(),
    dpWdDayOfMonth: z.number().min(1).max(31).optional(),
    dpWdAmount: z.number().min(0).optional(),
    dpIpsWithdrawalAge: z
      .number()
      .int()
      .min(62, "Uttaksalder må være minst 62 år")
      .optional(),
    dpIpsAnnualSavings: z
      .number()
      .min(0, "Årlig sparebeløp kan ikke være negativt")
      .max(25_000, "Årlig sparebeløp kan ikke overstige 25 000 kr")
      .optional(),
    askToAsk: z.boolean().default(false),
    moveAll: z.boolean().default(false),
    portDynamicSell: z.boolean().default(false),
    portDynamicWd: z.boolean().default(false),
    moveFrom: z.string().optional(),
    moveFromId: z.string().optional(),
    Instrument1: z.string().optional(),
    InstrumentAmt1: z.number().min(0).optional(),
    Instrument2: z.string().optional(),
    InstrumentAmt2: z.number().min(0).optional(),
    Instrument3: z.string().optional(),
    InstrumentAmt3: z.number().min(0).optional(),
    Instrument4: z.string().optional(),
    InstrumentAmt4: z.number().min(0).optional(),
    Instrument5: z.string().optional(),
    InstrumentAmt5: z.number().min(0).optional(),
    Instrument6: z.string().optional(),
    InstrumentAmt6: z.number().min(0).optional(),
  })
  .superRefine((data, ctx) => {
    // Bank account: must either select an existing account or enter a new one
    const hasExistingAccount =
      data.dpAccount &&
      data.dpAccount !== "" &&
      data.dpAccount !== "newAccount";
    const hasNewAccount =
      data.dpAccount === "newAccount" && !!data.dpNewAccount;
    if (!hasExistingAccount && !hasNewAccount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          data.dpAccount === "newAccount"
            ? "New account number is required"
            : "Bank account is required",
        path: ["dpAccount"],
      });
    }

    if (data.dpOnOffAmount > 0 && !data.dpOnOffDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Date is required when one-off amount is greater than 0",
        path: ["dpOnOffDate"],
      });
    }
    if (data.dpSavingsEnabled) {
      if (!data.dpSavingsAmount || data.dpSavingsAmount <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Savings amount is required",
          path: ["dpSavingsAmount"],
        });
      }
      if (!data.dpSavingsDayOfMonth) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Debit date is required",
          path: ["dpSavingsDayOfMonth"],
        });
      }
    }
    if (data.dpWithdrawalEnabled) {
      if (!data.dpWdAccount || data.dpWdAccount === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Account is required",
          path: ["dpWdAccount"],
        });
      }
      if (!data.dpWdDayOfMonth) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Withdrawal date is required",
          path: ["dpWdDayOfMonth"],
        });
      }
      if (!data.dpWdAmount || data.dpWdAmount <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Withdrawal amount is required",
          path: ["dpWdAmount"],
        });
      }
    }
    if (data.dpPortType === "23") {
      if (data.dpIpsWithdrawalAge === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Uttaksalder er påkrevd",
          path: ["dpIpsWithdrawalAge"],
        });
      }
      if (data.dpIpsAnnualSavings === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Årlig sparebeløp er påkrevd",
          path: ["dpIpsAnnualSavings"],
        });
      }
    }
  });

export type FeeType = "onlyFund" | "all";
export type OrdreModul = "yes" | "no";

export type OrderFormData = Omit<
  z.infer<typeof orderFormSchema>,
  "dpFeeType" | "ordreModul"
> & {
  dpFeeType: FeeType;
  ordreModul: OrdreModul;
  buyOrders: BuyOrderRow[];
  sellOrders: SellOrderRow[];
};
